import React, { useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import Div100vh from 'react-div-100vh';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import PhoneRotate from '@/v1/components/atoms/Icon/PhoneRotate';
import DesktopExpand from '@/v1/components/atoms/Icon/DesktopExpand';
import Text from '@/v1/components/atoms/Text';
import LowPowerWarning from '@/v1/components/atoms/LowPowerWarning';
import { StateContext } from '@/v1/state/store';
import './Device.scss';

const propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

const defaultProps = {};

const Device = ({ children, data }) => {
    const [screenOrientation, setScreenOrientation] = useState(null);
    const [renderHeading, setRenderHeading] = useState(false);

    const deviceContainer = React.useRef();

    const store = useContext(StateContext);
    const { state, dispatch } = store;

    let timer = null;

    const deviceClassName = classNames('Device', {
        'Device--light': state.lightBgEnabled,
    });

    const init = () => {
        const orientation = window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
        setScreenOrientation(orientation);
    };

    const isTouch = () => {
        try {
            document.createEvent('TouchEvent');
            return true;
        } catch (e) {
            return false;
        }
    };

    // NBLEX-837
    // Force repaint, since iOS is not repainting
    // properly on orientation change
    const resetHeight = () => {
        const container = deviceContainer.current && deviceContainer.current.parentNode;
        const containerHeight = container.offsetHeight;

        container.style.height = `${containerHeight + 1}px`;
        setTimeout(() => {
            container.style.height = `${containerHeight}px`;
        }, 1);
    };

    const updateScreenOrientation = () => {
        if (window.matchMedia('(orientation: portrait)').matches) {
            if (screenOrientation !== 'portrait') dispatch({ type: 'SHOW_DEVICE_OVERLAY' });
            setScreenOrientation('portrait');
        }

        if (window.matchMedia('(orientation: landscape)').matches) {
            if (screenOrientation !== 'landscape') dispatch({ type: 'HIDE_DEVICE_OVERLAY' });
            setScreenOrientation('landscape');
        }

        setTimeout(resetHeight, 100);
    };

    const dispatchResizeEvent = () => {
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 1);
    };
    useEffect(() => {
        init();
        const debouncedUpdateScreenOrientation = debounce(updateScreenOrientation, 100);
        const debouncedDispatchResizeEvent = debounce(dispatchResizeEvent, 100);
        window.addEventListener('orientationchange', debouncedDispatchResizeEvent);
        window.addEventListener('resize', debouncedUpdateScreenOrientation);

        timer = setTimeout(() => {
            setRenderHeading(true);
        }, 200);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('orientationchange', debouncedDispatchResizeEvent);
            window.removeEventListener('resize', debouncedUpdateScreenOrientation);
        };
    }, []);

    if (data) {
        const contentClasses = classNames({
            Device__content: true,
            'Device__content--hide': screenOrientation === 'landscape',
        });

        const containerClasses = classNames({
            Device__container: true,
            'Device__container--show': screenOrientation === 'landscape',
        });

        const rotateClasses = classNames({
            Device__rotate: true,
            'Device__rotate--show': screenOrientation === 'portrait' && isTouch(),
        });

        const expandClasses = classNames({
            Device__expand: true,
            'Device__expand--show': screenOrientation === 'portrait' && !isTouch(),
        });

        return (
            <Div100vh className={deviceClassName}>
                <div className={contentClasses} ref={deviceContainer}>
                    <div className={rotateClasses}>
                        <div className="Device__icon">
                            <PhoneRotate />
                        </div>
                        {renderHeading && (
                            <div className="Device__heading">
                                <Text className="h4">{data.text.rotate}</Text>
                            </div>
                        )}
                    </div>
                    <div className={expandClasses}>
                        <div className="Device__icon">
                            <DesktopExpand speed={0.5} />
                        </div>
                        {renderHeading && (
                            <div className="Device__heading">
                                <Text className="h4">{data.text.expand}</Text>
                            </div>
                        )}
                    </div>
                </div>

                <div className={containerClasses}>{children}</div>

                <LowPowerWarning />
            </Div100vh>
        );
    }

    return null;
};

Device.propTypes = propTypes;
Device.defaultProps = defaultProps;

export default Device;
